export interface AddressOption {
  address: string;
  /** Where the address comes from: "Project address" or a subproject's name. */
  source: string;
}

export const PROJECT_ADDRESS_SOURCE = 'Project address';

// Matches SubProject.address (CharField max_length=255). Project.address is
// unbounded, so a longer project address must never reach this field.
export const SUBPROJECT_ADDRESS_MAX_LENGTH = 255;

interface AddressSource {
  id: string;
  name: string;
  address?: string | null;
}

/**
 * Every distinct address already associated with a project, for the subproject
 * address dropdown: the project's own address first, then each subproject's in
 * the order given. Addresses are trimmed, empty ones dropped, and duplicates
 * (compared case-insensitively) collapse onto their first occurrence, so the
 * project caption wins over a subproject that shares its address. Addresses
 * too long for a subproject to store are left out.
 *
 * `excludeSubprojectId` leaves out the subproject being edited — its address
 * is already the field's value.
 */
export function buildAddressOptions(
  projectAddress: string | null | undefined,
  subprojects: readonly AddressSource[],
  excludeSubprojectId?: string,
): AddressOption[] {
  const options: AddressOption[] = [];
  const seen = new Set<string>();

  const add = (address: string | null | undefined, source: string) => {
    const trimmed = (address ?? '').trim();
    if (!trimmed || trimmed.length > SUBPROJECT_ADDRESS_MAX_LENGTH) return;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    options.push({ address: trimmed, source });
  };

  add(projectAddress, PROJECT_ADDRESS_SOURCE);
  for (const sub of subprojects) {
    if (excludeSubprojectId && sub.id === excludeSubprojectId) continue;
    add(sub.address, sub.name);
  }
  return options;
}
